import { Building2, Clock, GripVertical, X } from 'lucide-react';
import IconButton from 'components/crm/ui/IconButton';
import Tag from 'components/crm/ui/Tag';
import useText from 'hooks/useText';
import { HOVER_REVEAL_CLASS, HOVER_ROW_CLASS } from 'utils/surfaceClasses';
import { attemptTag } from 'pages/prospection/callListItems';

// Tag tone of a call type: a follow-up is orange, a discovery primary.
export const TYPE_TONES = { discovery: 'primary', followup: 'orange' };

// The town of the company under its name, « - » when unknown (its source,
// « Téléphone », said nothing useful in a list of calls).
export const sublineOf = (text, company) => (company && company.city) || '-';

// The company block shared by a row to call and a call made; `faded` greys the
// name and the subline, `title` replaces the name.
export const CompanyCell = ({ company, text, faded, title }) => (
  <>
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-line bg-white text-ink">
      <Building2 size={18} />
    </span>
    <span
      className={`flex min-w-0 flex-1 flex-col gap-0.5 font-title ${faded ? 'opacity-50' : ''}`}
    >
      <span className="truncate text-sm font-medium leading-5 text-ink">
        {title || (company && company.name) || '-'}
      </span>
      <span className="truncate text-xs leading-4 text-body">
        {sublineOf(text, company)}
      </span>
    </span>
  </>
);

/**
 * One call left to make, 64 px high: its rank (`rank`) or a clock for a
 * planned one, ringed in orange when `selected`; the company, the attempt tag,
 * then, when given, the remove cross on hover (`onRemove`): calls start with
 * « Lancer » and leave the list through « Modifier ». `dragProps` (useDragReorder)
 * makes the row one to move in the queue, by its handle; `dragging` dims it
 * while it moves. `status` replaces the attempt tag: « En cours » (`current`),
 * « Suivant » (`next`), none (null). The row itself takes no click.
 */
const CallRow = ({
  line,
  rank,
  now,
  selected,
  removeDisabled,
  dragProps,
  dragging = false,
  status,
  onRemove,
}) => {
  const { text } = useText('prospection');
  const company = line.company || {};
  const title =
    line.kind === 'relaunch'
      ? text('row.relaunch', { company: company.name || '-' })
      : undefined;
  const tag = attemptTag(line, now);
  const label = text(tag.label.key, tag.label.values);
  const tagText = tag.suffix
    ? text(tag.suffix.key, { label, ...tag.suffix.values })
    : label;
  const removeLabel = text(
    removeDisabled ? 'row.removeDisabled' : 'row.remove',
  );

  return (
    <div
      data-testid="call-row"
      data-selected={selected ? 'true' : 'false'}
      {...dragProps}
      className={`${HOVER_ROW_CLASS} flex h-16 items-center gap-3 border-b border-line last:border-b-0 ${selected ? 'bg-[#fcfcfc]' : 'bg-white'} ${dragging ? 'opacity-50' : ''}`}
    >
      <span
        data-testid="call-row-rank"
        className={`flex size-7 shrink-0 items-center justify-center rounded-full border bg-white font-title text-xs font-medium ${selected ? 'border-primary text-primary-dark' : 'border-line text-ink'}`}
      >
        {rank ? rank : <Clock size={14} />}
      </span>
      <CompanyCell company={company} text={text} title={title} />
      {status === undefined ? (
        <Tag tone={TYPE_TONES[tag.tone] || 'primary'} title>
          {tagText}
        </Tag>
      ) : (
        status && (
          <Tag tone={status === 'current' ? 'successSoft' : 'primary'} title>
            {status === 'current' ? 'En cours' : 'Suivant'}
          </Tag>
        )
      )}
      {/* The handle to move it in the queue, at the end of the row, seen on hover. */}
      {dragProps && (
        <span aria-hidden="true" className={`flex shrink-0 cursor-grab text-faint ${HOVER_REVEAL_CLASS}`}>
          <GripVertical size={16} />
        </span>
      )}
      {onRemove && (
        <span className={`flex shrink-0 ${HOVER_REVEAL_CLASS}`}>
          <IconButton
            icon={X}
            label={removeLabel}
            disabled={removeDisabled}
            onClick={() => onRemove(line)}
            className="disabled:cursor-not-allowed disabled:opacity-40"
          />
        </span>
      )}
    </div>
  );
};

export default CallRow;
